import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { PauseCover } from "aic";
import { useAppSettings } from "contexts/AppSettingsContext";
import ServerDownPage from "pages/ServerDownPage";
import {
  createContext,
  useCallback,
  useEffect,
  useEffectEvent,
  useState,
  useTransition,
} from "react";
import { connectToHub } from "./signalr/connectToHub";
import {
  HubGroupMessage,
  MessageType,
  SignalRContextProps,
  TlfUpdateActionSignal,
  TocActionSignal,
  TocSaveSignal,
} from "./signalr/interface";

export const SignalRContext = createContext<SignalRContextProps | null>(null);

/**
 * Provides SignalR connection and messaging functionality to child components.
 *
 * @param params - The component props
 * @param params.children - The child components to render within the provider
 */
export const SignalRProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const appSettings = useAppSettings();
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [joinGroupList, setJoinGroupList] = useState<Set<string>>(new Set<string>());
  const [groups, setGroups] = useState<Set<string>>(new Set<string>());
  const [pending, startTransition] = useTransition();
  const MAX_SIGNALR_CONNECTION_ATTEMPTS = 3;
  // Max is 32K, however we still get errors as 32000, so reducing further
  const MAX_SIGNALR_MESSAGE_LENGTH = 28000;

  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [connectionAttempts, setConnectionAttempts] = useState<number>(0);
  /**
   * Connect to SignalR hub asynchronously.
   */
  const connectToHubAsync = useEffectEvent(async () => {
    /* istanbul ignore else */
    if (
      appSettings.appSettingsLoaded &&
      appSettings.displayName &&
      (!connection || connection.state === HubConnectionState.Disconnected)
    ) {
      try {
        setIsConnecting(true);
        setConnectionAttempts(connectionAttempts + 1);
        const connection = await connectToHub(`${appSettings.backEndUrl}hubs/notifications`);
        setConnection(connection);

        /**
         * Processes incoming SignalR messages and updates group membership.
         *
         * @param message - The message string to process
         */
        const processSignalRMessage = (message: string) => {
          const messageDetails: HubGroupMessage = JSON.parse(message);
          if (connection && messageDetails.displayName === connection.connectionId) {
            return;
          }
          console.debug("Received message from group:", messageDetails.groupName, messageDetails);
          if (messageDetails.messageType === "joinGroup") {
            setGroups((prev) => new Set(prev).add(messageDetails.groupName));
            return;
          }
        };
        if (connection && connection.state === HubConnectionState.Connected) {
          connection.on("SendMessageToGroup", processSignalRMessage);
        }
      } catch (error: unknown) {
        /* istanbul ignore next */
        console.warn("Error starting SignalR:", error);
      } finally {
        setIsConnecting(false);
      }
    }
  });

  /**
   * Check message size before sending to the group, then add the message to the list of sent messages.
   * @param message Message to send
   * @param groupName Name of the group to send the message to
   */
  const checkBeforeSendToGroup = useCallback(
    async (message: HubGroupMessage, groupName: string) => {
      /* istanbul ignore else */
      if (connection) {
        message.displayName = connection.connectionId!;
        const len = JSON.stringify(message).length;
        if (len > MAX_SIGNALR_MESSAGE_LENGTH) {
          message.messageType = "tooBig";
          message.text = ["{}"];
        }
        console.debug(
          "Sending message ",
          message.messageId,
          "to group:",
          groupName,
          " from:",
          message.displayName,
          " - ",
          connection.connectionId,
        );
        // Wait before sending to allow for state updates
        // Wait for the connection to be connected before invoking.
        /**
         * Wait until the given HubConnection reaches `Connected` state or the timeout expires.
         *
         * @param conn - HubConnection to wait for
         * @param timeout - Timeout in milliseconds
         * @returns true if connected within timeout, false otherwise
         */
        const waitForConnected = async (conn: HubConnection, timeout = 2000) => {
          const start = Date.now();
          while (conn.state !== HubConnectionState.Connected && Date.now() - start < timeout) {
            await new Promise((r) => setTimeout(r, 25));
          }
          return conn.state === HubConnectionState.Connected;
        };

        try {
          const ready = await waitForConnected(connection);
          /* istanbul ignore next */
          if (!ready) {
            console.warn("Connection not ready to send message");
            return;
          }
          await connection.invoke("SendMessageToGroup", JSON.stringify(message), groupName);
        } catch (err) {
          console.warn("Error sending message to group!", err);
          console.group("Message to group failed");
          console.warn("Message length: ", JSON.stringify(message).length);
          console.warn("Message: ", message);
          console.groupEnd();
        }
      }
    },
    [connection],
  );

  /**
   * Send a message to a specific group.
   * @param message The message to send
   * @param groupName The name of the group to send the message to
   * @param messageType The type of the message, defaults to "message"
   * @returns A promise that resolves when the message is sent
   */
  const sendMessageToGroup = useCallback(
    async (message: string, groupName: string, messageType: MessageType) => {
      const newMessage: HubGroupMessage = {
        messageId: crypto.randomUUID(),
        groupName,
        displayName: "",
        timeStamp: Date.now(),
        messageType,
        text: message.split("\n").map((m) => m.trim()),
      };
      await checkBeforeSendToGroup(newMessage, groupName);
    },
    [checkBeforeSendToGroup],
  );

  /**
   * Send a Table of Contents (ToC) update to a specific group.
   * @param actionSignal The ToC action signal containing the update details
   * @param groupName The name of the group to send the update to
   * @returns A promise that resolves to the HubGroupMessage sent
   */
  const sendTocUpdateToGroup = useCallback(
    async (actionSignal: TocActionSignal, groupName: string): Promise<HubGroupMessage> => {
      const newMessage: HubGroupMessage = {
        messageId: crypto.randomUUID(),
        groupName,
        displayName: "",
        timeStamp: Date.now(),
        messageType: "tocUpdate",
        text: JSON.stringify(actionSignal)
          .split("\n")
          .map((m) => m.trim()),
      };
      await checkBeforeSendToGroup(newMessage, groupName);
      return newMessage;
    },
    [checkBeforeSendToGroup],
  );

  /**
   * Send a TLF (Table of Contents) update to a specific group.
   * @param actionSignal The TLF update action signal containing the update details
   * @param groupName The name of the group to send the update to
   * @returns A promise that resolves to the HubGroupMessage sent
   */
  const sendTlfUpdateToGroup = useCallback(
    async (actionSignal: TlfUpdateActionSignal, groupName: string): Promise<HubGroupMessage> => {
      const newMessage: HubGroupMessage = {
        messageId: crypto.randomUUID(),
        groupName,
        displayName: "",
        timeStamp: Date.now(),
        messageType: "tlfUpdate",
        text: JSON.stringify(actionSignal)
          .split("\n")
          .map((m) => m.trim()),
      };
      await checkBeforeSendToGroup(newMessage, groupName);
      return newMessage;
    },
    [checkBeforeSendToGroup],
  );

  /**
   * Send a Table of Contents (ToC) save to a specific group.
   * @param saveSignal The ToC save signal containing the new hierarchy
   * @param groupName The name of the group to send the save to
   * @returns A promise that resolves to the HubGroupMessage sent
   */
  const sendTocSaveToGroup = useCallback(
    async (saveSignal: TocSaveSignal, groupName: string): Promise<HubGroupMessage> => {
      const tocAction = { hierarchy: saveSignal.newHierarchy, items: [] };
      const newMessage: HubGroupMessage = {
        messageId: crypto.randomUUID(),
        groupName,
        displayName: "",
        timeStamp: Date.now(),
        messageType: "tocSave",
        text: JSON.stringify(tocAction)
          .split("\n")
          .map((m) => m.trim()),
      };
      await checkBeforeSendToGroup(newMessage, groupName);
      return newMessage;
    },
    [checkBeforeSendToGroup],
  );

  /**
   * Connect to a SignalR group.
   * @param group Name of the group to connect to
   * @returns A promise that resolves when the connection is established
   */
  const connectToGroup = useCallback(
    async (newGroup: string) => {
      /* istanbul ignore else */
      if (connection && !groups.has(newGroup) && !joinGroupList.has(newGroup)) {
        await connection.invoke(
          "JoinGroup",
          newGroup,
          `${appSettings.displayName}~${connection.connectionId}`,
        );
        startTransition(() => setJoinGroupList((prev) => new Set(prev).add(newGroup)));
        console.debug("Joining group:", newGroup);
      }
    },
    [appSettings.displayName, connection, groups, joinGroupList],
  );

  /**
   * Effect to connect to SignalR hub when displayName changes or connection is disconnected.
   */
  useEffect(() => {
    if (
      (!connection || connection.state === HubConnectionState.Disconnected) &&
      !isConnecting &&
      connectionAttempts < MAX_SIGNALR_CONNECTION_ATTEMPTS &&
      !pending
    ) {
      startTransition(() => connectToHubAsync());
    }
  }, [appSettings.displayName, connection, connectionAttempts, isConnecting, pending]);

  return connectionAttempts >= MAX_SIGNALR_CONNECTION_ATTEMPTS ? (
    <ServerDownPage />
  ) : !connection || connection.state !== HubConnectionState.Connected ? (
    <PauseCover text="Connecting to the server" />
  ) : (
    <SignalRContext.Provider
      value={{
        connectToGroup,
        connection,
        groups,
        joinGroupList,
        sendMessageToGroup,
        sendTlfUpdateToGroup,
        sendTocSaveToGroup,
        sendTocUpdateToGroup,
      }}
    >
      {children}
    </SignalRContext.Provider>
  );
};

SignalRProvider.displayName = "SignalRProvider";
